import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Emergent Google OAuth lands at /#session_id=<id>.
 * We extract it, exchange with the backend for a session cookie, then
 * redirect to the main app. Using useRef so StrictMode double-mount can't
 * run the exchange twice.
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { emergentCallback } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    const hash = location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/", { replace: true });
      return;
    }
    processed.current = true;
    const session_id = decodeURIComponent(match[1]);
    (async () => {
      try {
        await emergentCallback(session_id);
      } catch {
        /* fall through — user will be routed to login below */
      }
      // Clear the fragment so a refresh doesn't retry
      window.history.replaceState({}, "", "/");
      navigate("/", { replace: true });
    })();
  }, [location, emergentCallback, navigate]);

  return (
    <div className="min-h-screen bg-[#0b0f17] text-slate-200 flex items-center justify-center">
      <div className="font-mono-digit text-sm text-slate-400 animate-pulse">Signing you in…</div>
    </div>
  );
}
